import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { api, type ApiResponse } from "@/api/client";
import type { Destination } from "@ckgo2/shared/types";
import { ArrowLeft, Save, Loader2, FileText, MapPin, Utensils, Image, Settings } from "lucide-react";
import ImagePicker from "@/components/common/ImagePicker";
import { GalleryEditor } from "@/components/trips";
import {
  HighlightsEditor,
  RegionsEditor,
  StringArrayEditor,
  type Highlight,
  type Region,
} from "@/components/destinations";

interface PracticalInfo {
  visa?: string;
  currency?: string;
  language?: string;
  timezone?: string;
  electricity?: string;
  vaccination?: string;
}

interface DestinationFormData {
  name: string;
  nameSk: string;
  slug: string;
  continent: string;
  shortDescription: string;
  shortDescriptionSk: string;
  description: string;
  descriptionSk: string;
  bestTimeToVisit: string;
  climateInfo: string;
  practicalInfo: PracticalInfo;
  highlights: Highlight[];
  regions: Region[];
  cuisine: string;
  cuisineSk: string;
  activities: string[];
  whyWithUs: string[];
  featuredImage: string;
  galleryImages: Array<{ url: string; alt?: string }>;
  heroVideoUrl: string;
  metaTitle: string;
  metaDescription: string;
  sortOrder: number;
  isFeatured: boolean;
}

const CONTINENTS = [
  { value: "asia", label: "Asie" },
  { value: "africa", label: "Afrika" },
  { value: "south-america", label: "Jižní Amerika" },
  { value: "north-america", label: "Severní Amerika" },
  { value: "oceania", label: "Oceánie" },
  { value: "europe", label: "Evropa" },
];

const TABS = [
  { id: "basic", label: "Základní", icon: FileText },
  { id: "content", label: "Obsah", icon: MapPin },
  { id: "practical", label: "Praktické info", icon: Settings },
  { id: "cuisine", label: "Kuchyně & Aktivity", icon: Utensils },
  { id: "media", label: "Média", icon: Image },
];

export default function DestinationEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === "new";
  const [activeTab, setActiveTab] = useState("basic");

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<DestinationFormData>({
    defaultValues: {
      name: "",
      nameSk: "",
      slug: "",
      continent: "asia",
      shortDescription: "",
      shortDescriptionSk: "",
      description: "",
      descriptionSk: "",
      bestTimeToVisit: "",
      climateInfo: "",
      practicalInfo: {},
      highlights: [],
      regions: [],
      cuisine: "",
      cuisineSk: "",
      activities: [],
      whyWithUs: [],
      featuredImage: "",
      galleryImages: [],
      heroVideoUrl: "",
      metaTitle: "",
      metaDescription: "",
      sortOrder: 0,
      isFeatured: false,
    },
  });

  // Fetch destination if editing
  const { data: destination, isLoading } = useQuery({
    queryKey: ["destination", id],
    queryFn: () => api.get<ApiResponse<Destination>>(`/api/admin/destinations/${id}`),
    enabled: !isNew,
  });

  // Reset form when destination data loads
  useEffect(() => {
    if (destination?.data) {
      const d = destination.data;
      reset({
        name: d.name || "",
        nameSk: d.nameSk || "",
        slug: d.slug || "",
        continent: d.continent || "asia",
        shortDescription: d.shortDescription || "",
        shortDescriptionSk: d.shortDescriptionSk || "",
        description: d.description || "",
        descriptionSk: d.descriptionSk || "",
        bestTimeToVisit: d.bestTimeToVisit || "",
        climateInfo: d.climateInfo || "",
        practicalInfo: d.practicalInfo || {},
        highlights: d.highlights || [],
        regions: d.regions || [],
        cuisine: d.cuisine || "",
        cuisineSk: d.cuisineSk || "",
        activities: d.activities || [],
        whyWithUs: d.whyWithUs || [],
        featuredImage: d.featuredImage || "",
        galleryImages: (d.galleryImages || []).map((url: string) =>
          typeof url === "string" ? { url, alt: "" } : url
        ),
        heroVideoUrl: d.heroVideoUrl || "",
        metaTitle: d.metaTitle || "",
        metaDescription: d.metaDescription || "",
        sortOrder: d.sortOrder || 0,
        isFeatured: d.isFeatured || false,
      });
    }
  }, [destination, reset]);

  // Auto-generate slug from name
  const name = watch("name");
  useEffect(() => {
    if (isNew && name) {
      const slug = name
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setValue("slug", slug, { shouldDirty: false });
    }
  }, [name, isNew, setValue]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: DestinationFormData) => {
      // Transform galleryImages back to string array for API
      const apiData = {
        ...data,
        galleryImages: data.galleryImages.map(img => img.url),
      };

      if (isNew) {
        return api.post<ApiResponse<Destination>>("/api/admin/destinations", apiData);
      }
      return api.put<ApiResponse<Destination>>(`/api/admin/destinations/${id}`, apiData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["destinations"] });
      queryClient.invalidateQueries({ queryKey: ["destination", id] });
      navigate("/destinations");
    },
  });

  const onSubmit = async (data: DestinationFormData) => {
    await saveMutation.mutateAsync(data);
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/destinations"
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "Nová destinace" : "Upravit destinaci"}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew
                ? "Vytvořte novou destinaci"
                : `Úprava: ${destination?.data?.name}`}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
          className="btn-primary"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Ukládám...
            </>
          ) : (
            <>
              <Save className="h-5 w-5" />
              Uložit
            </>
          )}
        </button>
      </div>

      {/* Error message */}
      {saveMutation.error && (
        <div className="rounded-lg bg-error-50 p-4 text-error-700 dark:bg-error-900/30 dark:text-error-400">
          {saveMutation.error.message}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-4 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Form */}
      <form id="destination-form" onSubmit={handleSubmit(onSubmit)}>
        {/* Basic Tab */}
        {activeTab === "basic" && (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              {/* Basic info */}
              <div className="card">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Základní informace
                </h2>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="label">Název (CZ) *</label>
                    <input
                      {...register("name", { required: "Název je povinný" })}
                      type="text"
                      id="name"
                      className={`input ${errors.name ? "input-error" : ""}`}
                      placeholder="např. Vietnam"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-error-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="nameSk" className="label">Název (SK)</label>
                    <input
                      {...register("nameSk")}
                      type="text"
                      id="nameSk"
                      className="input"
                      placeholder="např. Vietnam"
                    />
                  </div>

                  <div>
                    <label htmlFor="slug" className="label">URL slug *</label>
                    <input
                      {...register("slug", { required: "Slug je povinný" })}
                      type="text"
                      id="slug"
                      className={`input ${errors.slug ? "input-error" : ""}`}
                      placeholder="např. vietnam"
                    />
                  </div>

                  <div>
                    <label htmlFor="continent" className="label">Kontinent *</label>
                    <select
                      {...register("continent")}
                      id="continent"
                      className="input"
                    >
                      {CONTINENTS.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Short description */}
              <div className="card">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Krátký popis (pro hero sekci)
                </h2>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="shortDescription" className="label">Krátký popis (CZ)</label>
                    <textarea
                      {...register("shortDescription")}
                      id="shortDescription"
                      rows={2}
                      className="input"
                      placeholder="Vietnam – to je Asie se vším všudy..."
                    />
                  </div>

                  <div>
                    <label htmlFor="shortDescriptionSk" className="label">Krátký popis (SK)</label>
                    <textarea
                      {...register("shortDescriptionSk")}
                      id="shortDescriptionSk"
                      rows={2}
                      className="input"
                      placeholder="Vietnam – to je Ázia so všetkým..."
                    />
                  </div>
                </div>
              </div>

              {/* Climate & Best Time */}
              <div className="card">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Klima a nejlepší období
                </h2>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="climateInfo" className="label">Klima</label>
                    <input
                      {...register("climateInfo")}
                      type="text"
                      id="climateInfo"
                      className="input"
                      placeholder="např. Tropické monzunové"
                    />
                  </div>

                  <div>
                    <label htmlFor="bestTimeToVisit" className="label">Nejlepší období</label>
                    <input
                      {...register("bestTimeToVisit")}
                      type="text"
                      id="bestTimeToVisit"
                      className="input"
                      placeholder="např. Únor - Červen"
                    />
                  </div>
                </div>
              </div>

              {/* Full description */}
              <div className="card">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Popis destinace
                </h2>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="description" className="label">Popis (CZ)</label>
                    <textarea
                      {...register("description")}
                      id="description"
                      rows={6}
                      className="input"
                      placeholder="Podrobný popis destinace..."
                    />
                  </div>

                  <div>
                    <label htmlFor="descriptionSk" className="label">Popis (SK)</label>
                    <textarea
                      {...register("descriptionSk")}
                      id="descriptionSk"
                      rows={6}
                      className="input"
                      placeholder="Podrobný popis destinácie..."
                    />
                  </div>
                </div>
              </div>

              {/* SEO */}
              <div className="card">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  SEO
                </h2>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="metaTitle" className="label">Meta title</label>
                    <input
                      {...register("metaTitle")}
                      type="text"
                      id="metaTitle"
                      className="input"
                      placeholder="SEO titulek"
                    />
                  </div>

                  <div>
                    <label htmlFor="metaDescription" className="label">Meta description</label>
                    <textarea
                      {...register("metaDescription")}
                      id="metaDescription"
                      rows={2}
                      className="input"
                      placeholder="Krátký popis pro vyhledávače (max 160 znaků)"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="card">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Hlavní obrázek
                </h2>
                <Controller
                  name="featuredImage"
                  control={control}
                  render={({ field }) => (
                    <ImagePicker
                      value={field.value || ""}
                      onChange={field.onChange}
                      folder="destinations"
                    />
                  )}
                />
              </div>

              <div className="card">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Nastavení
                </h2>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="sortOrder" className="label">Pořadí řazení</label>
                    <input
                      {...register("sortOrder", { valueAsNumber: true })}
                      type="number"
                      id="sortOrder"
                      className="input"
                      min={0}
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      {...register("isFeatured")}
                      type="checkbox"
                      id="isFeatured"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="isFeatured" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Zobrazit jako featured
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Tab - Highlights & Regions */}
        {activeTab === "content" && (
          <div className="space-y-6">
            <div className="card">
              <Controller
                name="highlights"
                control={control}
                render={({ field }) => (
                  <HighlightsEditor
                    value={field.value || []}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className="card">
              <Controller
                name="regions"
                control={control}
                render={({ field }) => (
                  <RegionsEditor
                    value={field.value || []}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>
        )}

        {/* Practical Info Tab */}
        {activeTab === "practical" && (
          <div className="card">
            <h2 className="mb-6 text-lg font-semibold text-gray-900 dark:text-white">
              Praktické informace
            </h2>

            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <label className="label">Vízum</label>
                <textarea
                  {...register("practicalInfo.visa")}
                  rows={3}
                  className="input"
                  placeholder="Informace o vízu..."
                />
              </div>

              <div>
                <label className="label">Měna</label>
                <input
                  {...register("practicalInfo.currency")}
                  type="text"
                  className="input"
                  placeholder="např. Vietnamský dong (VND)"
                />
              </div>

              <div>
                <label className="label">Jazyk</label>
                <input
                  {...register("practicalInfo.language")}
                  type="text"
                  className="input"
                  placeholder="např. Vietnamština"
                />
              </div>

              <div>
                <label className="label">Časové pásmo</label>
                <input
                  {...register("practicalInfo.timezone")}
                  type="text"
                  className="input"
                  placeholder="např. UTC+7"
                />
              </div>

              <div>
                <label className="label">Elektřina</label>
                <input
                  {...register("practicalInfo.electricity")}
                  type="text"
                  className="input"
                  placeholder="např. 220V, typ A/C/G"
                />
              </div>

              <div>
                <label className="label">Očkování</label>
                <textarea
                  {...register("practicalInfo.vaccination")}
                  rows={3}
                  className="input"
                  placeholder="Doporučená očkování..."
                />
              </div>
            </div>
          </div>
        )}

        {/* Cuisine & Activities Tab */}
        {activeTab === "cuisine" && (
          <div className="space-y-6">
            <div className="card">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                Gastronomie
              </h2>

              <div className="space-y-4">
                <div>
                  <label htmlFor="cuisine" className="label">Popis kuchyně (CZ)</label>
                  <textarea
                    {...register("cuisine")}
                    id="cuisine"
                    rows={5}
                    className="input"
                    placeholder="Vietnamská kuchyně je proslulá svou čerstvostí..."
                  />
                </div>

                <div>
                  <label htmlFor="cuisineSk" className="label">Popis kuchyně (SK)</label>
                  <textarea
                    {...register("cuisineSk")}
                    id="cuisineSk"
                    rows={5}
                    className="input"
                    placeholder="Vietnamská kuchyňa je preslávená svojou čerstvosťou..."
                  />
                </div>
              </div>
            </div>

            <div className="card">
              <Controller
                name="activities"
                control={control}
                render={({ field }) => (
                  <StringArrayEditor
                    value={field.value || []}
                    onChange={field.onChange}
                    title="Aktivity"
                    description="Přidejte aktivity, které lze v destinaci zažít"
                    placeholder="např. Plavba po zátoce Ha Long"
                    emptyMessage="Zatím žádné aktivity"
                    addButtonText="Přidat"
                  />
                )}
              />
            </div>

            <div className="card">
              <Controller
                name="whyWithUs"
                control={control}
                render={({ field }) => (
                  <StringArrayEditor
                    value={field.value || []}
                    onChange={field.onChange}
                    title="Proč cestovat s námi"
                    description="Výhody cestování do této destinace s CK Go2"
                    placeholder="např. Česky mluvící průvodci v Hanoji"
                    emptyMessage="Zatím žádné výhody"
                    addButtonText="Přidat"
                  />
                )}
              />
            </div>
          </div>
        )}

        {/* Media Tab */}
        {activeTab === "media" && (
          <div className="space-y-6">
            <div className="card">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                Hlavní obrázek
              </h2>
              <Controller
                name="featuredImage"
                control={control}
                render={({ field }) => (
                  <ImagePicker
                    value={field.value || ""}
                    onChange={field.onChange}
                    folder="destinations"
                  />
                )}
              />
            </div>

            <div className="card">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                Video v hero sekci (YouTube URL)
              </h2>
              <input
                {...register("heroVideoUrl")}
                type="text"
                className="input"
                placeholder="https://youtube.com/watch?v=..."
              />
            </div>

            <div className="card">
              <Controller
                name="galleryImages"
                control={control}
                render={({ field }) => (
                  <GalleryEditor
                    value={field.value || []}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
